import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import IconOnlyButton from 'components/Buttons/IconOnlyButton';
import Button from 'components/Buttons/Button';
import DirectorySearch from 'components/Search';
import useTrapFocusInsideModal from 'utils/hooks/useTrapFocusInsideModal';
import LoadingZone from '../LoadingZone';
import useProfileData from '../../utils/hooks/useProfileData';
import Notifications from './UserProfileMenu';
import useWindowSize from '../../utils/hooks/useWindowSize';
import logoHeart from '../../images/logos/wn-logo-heart-gold.svg';
import logoWord from '../../images/logos/wn-logo-text-white-gold.svg';
import NavBarSkeleton from './NavBarSkeleton';
import { userConstituenciesSelector, fnSignOut } from './helpers';
import fnBuildNavOptions from './fnBuildNavOptions';

const NavBar = () => {
  const location = useLocation();
  const isOnTheHub =
    location.pathname === '/main' || location.pathname.startsWith('/main/');
  const isProfileDataLoading = useProfileData();
  const windowSize = useWindowSize();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const volunteerTypes = useSelector(
    (state) => state?.Profile?.BIOGRAPHICAL?.VOLUNTEERTYPES?.TYPE || []
  );
  const aUserConstituencies = useSelector(userConstituenciesSelector);
  const isScholarshipRecipient = useSelector((state) =>
    isProfileDataLoading
      ? false
      : state.Profile.BIOGRAPHICAL.ISSCHOLARSHIPRECIPIENT
  );

  const navRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const searchToggleRef = useRef(null);
  const isMobileView = windowSize.width < 1366;
  const isMobileSmall = windowSize.width < 640;
  const bShowCheeseburgerMenu = !isOnTheHub || isMobileSmall;

  const handleMobileNavToggle = () => setIsMobileNavOpen(!isMobileNavOpen);
  const handleNavLinkClick = () => setIsMobileNavOpen(false);

  const handleOpenMobileSearch = () => setIsMobileSearchOpen(true);
  const handleCloseMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false);
    searchToggleRef.current?.focus();
  }, []);

  // Reset mobile search when resizing past breakpoint
  useEffect(() => {
    if (!isMobileSmall) setIsMobileSearchOpen(false);
  }, [isMobileSmall]);

  const closeMobileNav = useCallback(() => {
    setIsMobileNavOpen(false);
    toggleButtonRef.current?.focus();
  }, []);

  // Trap focus inside mobile nav, auto-focus first link, close on Escape
  useTrapFocusInsideModal(navRef, {
    bEnabled: isMobileNavOpen && isMobileView,
    fnOnEscape: closeMobileNav,
  });

  const navOptions = useMemo(
    () =>
      fnBuildNavOptions(
        volunteerTypes,
        aUserConstituencies,
        isScholarshipRecipient
      ),
    [volunteerTypes, aUserConstituencies, isScholarshipRecipient]
  );

  const mobileHeaderClass = `navBar__mobileHeader${
    isMobileSearchOpen ? ' navBar__mobileHeader--searchOpen' : ''
  }`;

  return (
    <header className={`navBar${isOnTheHub ? ' navBar--theHub' : ''}`}>
      <div className={mobileHeaderClass}>
        <div className='navBar__mobileHeaderLeft'>
          {bShowCheeseburgerMenu && (
            <IconOnlyButton
              sIcon='menu'
              fnHandleClick={handleMobileNavToggle}
              className='navBar__toggle navBar__hamburger'
              sAriaLabel={isMobileNavOpen ? 'Close main menu' : 'Open main menu'}
              bAriaExpanded={isMobileNavOpen}
              oRef={toggleButtonRef}
              sFlavor='tertiary'
            />
          )}
          <IconOnlyButton
            sIcon='search'
            fnHandleClick={handleOpenMobileSearch}
            className='navBar__searchToggle'
            sAriaLabel='Open search'
            sFlavor='tertiary'
            oRef={searchToggleRef}
          />
        </div>
        <IconOnlyButton
          sIcon='close'
          fnHandleClick={handleCloseMobileSearch}
          className='navBar__searchClose'
          sAriaLabel='Close search'
          sFlavor='tertiary'
        />
        <div className='navBar__mobileLogo'>
          <Link to='/main' onClick={handleNavLinkClick}>
            <img
              className='navBar__mobileLogoHeart'
              src={logoHeart}
              alt='Heart with a top hat inside'
            />
            <img
              className='navBar__mobileLogoWord'
              src={logoWord}
              alt='Wake Network'
            />
          </Link>
        </div>
        <div className='navBar__search'>
          <DirectorySearch
            isMobileExpanded={isMobileSearchOpen}
            bIsMobile={isMobileSmall}
            fnCloseMobileSearch={handleCloseMobileSearch}
          />
        </div>
        <div className='navBar__mobileNotLogoWrapper'>
          {(isOnTheHub || isMobileView) && !isProfileDataLoading && (
            <Notifications />
          )}
        </div>
      </div>
      {bShowCheeseburgerMenu && (
        <nav
          ref={navRef}
          className={`navBar__menu ${
            isMobileNavOpen ? 'navBar__menu--open' : ''
          }`}
          aria-label='Main menu'
          aria-hidden={isMobileView ? !isMobileNavOpen : undefined}
        >
          <IconOnlyButton
            sIcon='close'
            fnHandleClick={handleMobileNavToggle}
            className='navBar__toggle navBar__toggle--close'
            sAriaLabel='Close main menu'
            sFlavor='tertiary'
          />
          <ul className='navBar__list'>
            <li className='navBar__listItem navBar__listItem--logo'>
              <Link to='/main' onClick={handleNavLinkClick}>
                <img
                  className='navBar__logoHeart'
                  src={logoHeart}
                  alt='Heart with a top hat inside'
                />
                <img
                  className='navBar__logoWord'
                  src={logoWord}
                  alt='Wake Network'
                />
              </Link>
            </li>
            <LoadingZone
              isLoading={isProfileDataLoading}
              skeleton={<NavBarSkeleton />}
            >
              {navOptions.map((option) => (
                <li key={option.path} className='navBar__listItem'>
                  <Link
                    to={option.path}
                    className='navBar__link'
                    onClick={handleNavLinkClick}
                  >
                    {option.label}
                  </Link>
                </li>
              ))}
              <li className='navBar__listItem'>
                <Button fnHandleClick={fnSignOut} className='navBar__link'>
                  Sign Out
                </Button>
              </li>
              {windowSize.width >= 1366 && (
                <li className='navBar__listItem'>
                  <Notifications />
                </li>
              )}
            </LoadingZone>
          </ul>
        </nav>
      )}
    </header>
  );
};

export default NavBar;
